import { api, CancelToken, getApiErrorMessage } from "../../helpers";
import { getApiAdditionalListingInfo } from "../../constants";

import dayjs from "dayjs";
import { createSelector } from "reselect";

const minutesToExpire = 5;

/**
 * Action types
 * @property {string} INFO_CLEAR
 * @property {string} INFO_CLEAR_ERROR
 * @property {string} INFO_ERROR
 * @property {string} INFO_COVERAGE_ERROR
 * @property {string} INFO_INSPECTIONS_ERROR
 * @property {string} INFO_SERVICE_ERROR
 * @property {string} INFO_WARRANTY_ERROR
 * @property {string} INFO_LOADING
 * @property {string} INFO_COVERAGE_LOADING
 * @property {string} INFO_INSPECTIONS_LOADING
 * @property {string} INFO_SERVICE_LOADING
 * @property {string} INFO_WARRANTY_LOADING
 * @property {string} INFO_SUCCESS
 * @property {string} INFO_COVERAGE_SUCCESS
 * @property {string} INFO_INSPECTIONS_SUCCESS
 * @property {string} INFO_SERVICE_SUCCESS
 * @property {string} INFO_WARRANTY_SUCCESS
 */
export const types = {
  INFO_CLEAR: "INFO_CLEAR",
  INFO_CLEAR_ERROR: "INFO_CLEAR_ERROR",
  INFO_ERROR: "INFO_ERROR",
  INFO_COVERAGE_ERROR: "INFO_COVERAGE_ERROR",
  INFO_INSPECTIONS_ERROR: "INFO_INSPECTIONS_ERROR",
  INFO_SERVICE_ERROR: "INFO_SERVICE_ERROR",
  INFO_WARRANTY_ERROR: "INFO_WARRANTY_ERROR",
  INFO_LOADING: "INFO_LOADING",
  INFO_COVERAGE_LOADING: "INFO_COVERAGE_LOADING",
  INFO_INSPECTIONS_LOADING: "INFO_INSPECTIONS_LOADING",
  INFO_SERVICE_LOADING: "INFO_SERVICE_LOADING",
  INFO_WARRANTY_LOADING: "INFO_WARRANTY_LOADING",
  INFO_SUCCESS: "INFO_SUCCESS",
  INFO_COVERAGE_SUCCESS: "INFO_COVERAGE_SUCCESS",
  INFO_INSPECTIONS_SUCCESS: "INFO_INSPECTIONS_SUCCESS",
  INFO_SERVICE_SUCCESS: "INFO_SERVICE_SUCCESS",
  INFO_WARRANTY_SUCCESS: "INFO_WARRANTY_SUCCESS",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates additional listing info error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  additionalInfoError: (state = null, action) => {
    switch (action.type) {
      case types.INFO_ERROR:
        return action.error;
      case types.INFO_CLEAR:
      case types.INFO_CLEAR_ERROR:
      case types.INFO_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info coverage options error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  additionalInfoCoverageError: (state = null, action) => {
    switch (action.type) {
      case types.INFO_COVERAGE_ERROR:
        return action.error;
      case types.INFO_CLEAR:
      case types.INFO_CLEAR_ERROR:
      case types.INFO_COVERAGE_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info inspections error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  additionalInfoInspectionsError: (state = null, action) => {
    switch (action.type) {
      case types.INFO_INSPECTIONS_ERROR:
        return action.error;
      case types.INFO_CLEAR:
      case types.INFO_CLEAR_ERROR:
      case types.INFO_INSPECTIONS_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info service history error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  additionalInfoServiceError: (state = null, action) => {
    switch (action.type) {
      case types.INFO_SERVICE_ERROR:
        return action.error;
      case types.INFO_CLEAR:
      case types.INFO_CLEAR_ERROR:
      case types.INFO_SERVICE_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info warranty error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  additionalInfoWarrantyError: (state = null, action) => {
    switch (action.type) {
      case types.INFO_WARRANTY_ERROR:
        return action.error;
      case types.INFO_CLEAR:
      case types.INFO_CLEAR_ERROR:
      case types.INFO_WARRANTY_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  additionalInfoLoading: (state = false, action) => {
    switch (action.type) {
      case types.INFO_CLEAR:
      case types.INFO_ERROR:
      case types.INFO_SUCCESS:
        return false;
      case types.INFO_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info coverage options loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  additionalInfoCoverageLoading: (state = false, action) => {
    switch (action.type) {
      case types.INFO_CLEAR:
      case types.INFO_COVERAGE_ERROR:
      case types.INFO_COVERAGE_SUCCESS:
        return false;
      case types.INFO_COVERAGE_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info inspections loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  additionalInfoInspectionsLoading: (state = false, action) => {
    switch (action.type) {
      case types.INFO_CLEAR:
      case types.INFO_INSPECTIONS_ERROR:
      case types.INFO_INSPECTIONS_SUCCESS:
        return false;
      case types.INFO_INSPECTIONS_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info service history loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  additionalInfoServiceLoading: (state = false, action) => {
    switch (action.type) {
      case types.INFO_CLEAR:
      case types.INFO_SERVICE_ERROR:
      case types.INFO_SERVICE_SUCCESS:
        return false;
      case types.INFO_SERVICE_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info warranty loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  additionalInfoWarrantyLoading: (state = false, action) => {
    switch (action.type) {
      case types.INFO_CLEAR:
      case types.INFO_WARRANTY_ERROR:
      case types.INFO_WARRANTY_SUCCESS:
        return false;
      case types.INFO_WARRANTY_LOADING:
        return true;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.info
   * @memberof Reducers
   */
  additionalInfo: (state = [], action) => {
    switch (action.type) {
      case types.INFO_ERROR:
      case types.INFO_CLEAR:
        return [];
      case types.INFO_SUCCESS:
        return action.info;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info coverage options state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.info
   * @memberof Reducers
   */
  additionalInfoCoverage: (state = [], action) => {
    switch (action.type) {
      case types.INFO_COVERAGE_ERROR:
      case types.INFO_CLEAR:
        return [];
      case types.INFO_COVERAGE_SUCCESS:
        return action.info;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info inspections state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.info
   * @memberof Reducers
   */
  additionalInfoInspections: (state = [], action) => {
    switch (action.type) {
      case types.INFO_INSPECTIONS_ERROR:
      case types.INFO_CLEAR:
        return [];
      case types.INFO_INSPECTIONS_SUCCESS:
        return action.info;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info service history state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.info
   * @memberof Reducers
   */
  additionalInfoService: (state = [], action) => {
    switch (action.type) {
      case types.INFO_SERVICE_ERROR:
      case types.INFO_CLEAR:
        return [];
      case types.INFO_SERVICE_SUCCESS:
        return action.info;
      default:
        return state;
    }
  },

  /**
   * Updates additional listing info warranty state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.info
   * @memberof Reducers
   */
  additionalInfoWarranty: (state = [], action) => {
    switch (action.type) {
      case types.INFO_WARRANTY_ERROR:
      case types.INFO_CLEAR:
        return [];
      case types.INFO_WARRANTY_SUCCESS:
        return action.info;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Cancel additional listing info API request
   * * Updated with a new Axios cancel token after an API request is initiated
   * @memberof Actions
   */
  cancel: () => false,

  /**
   * Clear cached listings
   * @memberof Actions
   */
  clear: () => ({
    type: types.INFO_CLEAR,
    info: [],
  }),

  /**
   * Clear errors
   * @memberof Actions
   */
  clearError: () => ({
    type: types.INFO_CLEAR_ERROR,
    info: [],
  }),

  /**
   * Fetch additional listing info from API
   * @param {string} equipmentId
   * @memberof Actions
   */
  fetch: (equipmentId, dataType) => (dispatch, getState) => {
    const { infoLoading } = getState();

    if (infoLoading) {
      actions.cancel("Canceled");
    }

    dispatch(actions.handleLoading(dataType));

    const url = getApiAdditionalListingInfo(equipmentId, dataType);

    api
      .get(url, {
        cancelToken: new CancelToken(c => {
          actions.cancel = c;
        }),
      })
      .then(response => {
        if (!!response && !!response.data && !!response.data.data) {
          dispatch(actions.parse(equipmentId, response.data.data, dataType));
        } else {
          dispatch(actions.parseError(response, dataType));
        }
      })
      .catch(error => {
        dispatch(actions.parseError(error, dataType));
      });
  },

  /**
   * Handle error messages
   * @param {string} message
   * @memberof Actions
   */
  handleError: (message, dataType) => ({
    type: getAdditionalInfoAction("ERROR", dataType),
    error: message,
  }),

  /**
   * Handle additional listing info loading events
   * @memberof Actions
   */
  handleLoading: dataType => ({
    type: getAdditionalInfoAction("LOADING", dataType),
  }),

  /**
   * Handle additional listing API response
   * @param {array} listing
   * @memberof Actions
   */
  handleSuccess: (info, dataType) => ({
    type: getAdditionalInfoAction("SUCCESS", dataType),
    info: info,
  }),

  /**
   * Parse additional listing info API response
   * @param {*} data
   * @memberof Actions
   */
  parse: (equipmentId, data, dataType) => (dispatch, getState) => {
    try {
      data.equipmentId = equipmentId;
      data.expires = dayjs().add(minutesToExpire, "minutes").format();

      // Add to cache and clean up expired cache
      const state = getState();
      const additionalInfo = state[getAdditionalInfoState(dataType)];

      let found = false;
      let updatedInfo = additionalInfo.reduce((updated, info) => {
        if (info.equipmentId === equipmentId) {
          found = true;
          updated.push(data);
        } else if (dayjs().isBefore(info.expires)) {
          updated.push(info);
        }

        return updated;
      }, []);

      if (!found) {
        updatedInfo.push(data);
      }

      dispatch(actions.handleSuccess(updatedInfo, dataType));
    } catch (err) {
      dispatch(actions.handleError(err?.message || "Error", dataType));
    }
  },

  /**
   * Parse errors and dispatch error message
   * @param {*} error
   * @param {string} dataType
   * @memberof Actions
   */
  parseError: (error, dataType) => dispatch => {
    const message = getApiErrorMessage(error);
    dispatch(actions.handleError(message, dataType));
  },
};

/**
 * Get additional info action for data type
 */
function getAdditionalInfoAction(action = "ERROR", dataType) {
  switch (dataType) {
    case "coverageOptions":
      return types[`INFO_COVERAGE_${action}`];
    case "inspections":
      return types[`INFO_INSPECTIONS_${action}`];
    case "serviceHistory":
      return types[`INFO_SERVICE_${action}`];
    case "warranty":
      return types[`INFO_WARRANTY_${action}`];
    default:
      return types[`INFO_${action}`];
  }
}

/**
 * Get additional info state for data type
 */
export function getAdditionalInfoState(dataType) {
  const base = "additionalInfo";

  switch (dataType) {
    case "coverageOptions":
      return base + "Coverage";
    case "inspections":
      return base + "Inspections";
    case "serviceHistory":
      return base + "Service";
    case "warranty":
      return base + "Warranty";
    default:
      return base;
  }
}

/**
 * Select cached additional info value for the requested data type
 * @param {string} stateName
 * @param {string} equipmentId
 * @returns {object}
 */
export const selectCachedAdditionalInfo = createSelector(
  [
    (state, stateName) => state?.[stateName],
    (state, stateName, equipmentId) => equipmentId,
  ],
  (additionalInfo = [], equipmentId) => {
    const cachedInfo = additionalInfo.find(
      info => info.equipmentId === equipmentId
    );

    if (!cachedInfo || dayjs().isAfter(cachedInfo.expires)) {
      return null;
    } else {
      return cachedInfo;
    }
  }
);
